
// client/src/components/ui/PolicyBuilder.jsx
import React, { useState, useEffect } from "react";
import { POLICY_MODULES } from "../../../../data/origami/policySimulationData";

const API = process.env.REACT_APP_API_URL;

export default function PolicyBuilder({ onSimulate, onValidate, isSimulating, code, toast }) {
  const [selectedModules, setSelectedModules] = useState([]);
  const [validationChoice, setValidationChoice] = useState(null);
  const [simulations, setSimulations] = useState([]);

  // ✅ NOUVEAU : État de complétion
  const [isCompleted, setIsCompleted] = useState(() => {
    try {
      return localStorage.getItem(`policy_completed_${code}`) === "true";
    } catch {
      return false;
    }
  });

  const [hintText, setHintText] = useState(() => {
    try {
      return localStorage.getItem(`policy_hint_${code}`) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!code || !API) return;

    const fetchSimulations = async () => {
      try {
        const response = await fetch(`${API}/api/sessions/${code}`);
        const session = await response.json();

        const sims = session.infraState?.policySimulations || {};

        const simArray = Object.entries(sims).map(([id, data]) => ({
          id,
          modules: data?.modules || [],
          timestamp: data?.timestamp || new Date().toISOString(),
        }));

        setSimulations(simArray);
      } catch (error) {
        console.error("Error fetching simulations:", error);
      }
    };

    fetchSimulations();
    const interval = setInterval(fetchSimulations, 2000);

    return () => clearInterval(interval);
  }, [code, API]);

  const toggleModule = (moduleId) => {
    setSelectedModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(id => id !== moduleId);
      }

      if (prev.length >= 3) {
        return prev;
      }

      return [...prev, moduleId];
    });
  };

  const handleRunSimulation = async () => {
    if (selectedModules.length !== 3) return;

    const sortedNew = [...selectedModules].sort().join(",");
    const isDuplicate = simulations.some(sim => {
      const sortedExisting = [...(sim.modules || [])].sort().join(",");
      return sortedExisting === sortedNew;
    });

    if (isDuplicate) {
      toast?.error?.("Cette combinaison a déjà été testée !");
      return;
    }

    await onSimulate(selectedModules);
    setSelectedModules([]);
  };

  const handleValidate = async () => {
    if (!validationChoice) return;

    const result = await onValidate(validationChoice);

    // ✅ Si validation réussie
    if (result?.correct) {
      const hint = "Les simulations RH ont révélé un pattern récurrent : les marquages [T-X] dans le canal #origami-support semblent indiquer des niveaux de toxicité. Ces marqueurs pourraient former un code...";

      setIsCompleted(true);
      setHintText(hint);

      localStorage.setItem(`policy_completed_${code}`, "true");
      localStorage.setItem(`policy_hint_${code}`, hint);
    }
  };

  const groupedModules = POLICY_MODULES.reduce((acc, module) => {
    if (!acc[module.category]) acc[module.category] = [];
    acc[module.category].push(module);
    return acc;
  }, {});

  // ✅ ÉCRAN DE SUCCÈS
  if (isCompleted) {
    return (
      <div className="p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Success Header */}
          <div className="bg-emerald-500/20 border border-emerald-500/40 rounded-xl p-6 text-center">
            <div className="text-6xl mb-4">✅</div>
            <div className="text-2xl font-bold text-emerald-200 mb-2">
              Politique validée avec succès !
            </div>
            <div className="text-sm text-emerald-300">
              La combinaison optimale a été identifiée et transmise
            </div>
          </div>

          {/* Hint Révélé */}
          <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="text-3xl">💡</div>
              <div>
                <div className="text-lg font-semibold text-indigo-200 mb-1">
                  Indice débloqué
                </div>
                <div className="text-xs text-indigo-300">
                  Partagez cette information avec l'équipe
                </div>
              </div>
            </div>

            <div className="bg-slate-900/60 border border-indigo-500/20 rounded-lg p-4">
              <p className="text-sm text-slate-200 leading-relaxed">
                {hintText}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // INTERFACE NORMALE (si pas encore complété)
  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-slate-900/40 border border-slate-700 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xl font-bold text-white mb-1">
                Simulateur de Politique RH
              </div>
              <div className="text-sm text-slate-400">
                Sélectionnez 3 modules pour créer une combinaison à tester
              </div>
            </div>
            <div className="text-xs text-slate-500">
              v2.4.1-exec
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {Object.entries(groupedModules).map(([category, modules]) => (
            <div key={category} className="space-y-2">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide px-2">
                {category}
              </div>
              {modules.map(module => {
                const isSelected = selectedModules.includes(module.id);
                const isDisabled = !isSelected && selectedModules.length >= 3;

                return (
                  <button
                    key={module.id}
                    onClick={() => !isDisabled && toggleModule(module.id)}
                    disabled={isDisabled}
                    className={`
                      w-full text-left p-3 rounded-lg border transition text-sm
                      ${isSelected
                        ? "bg-purple-500/20 border-purple-400/60 text-purple-100"
                        : isDisabled
                          ? "bg-slate-900/30 border-slate-700/50 text-slate-600 cursor-not-allowed"
                          : "bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-600"
                      }
                    `}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium">{module.name}</span>
                      {isSelected && (
                        <span className="text-purple-300 text-xs">✓</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="bg-slate-900/40 border border-slate-700 rounded-xl p-4">
          <div className="text-sm font-semibold text-slate-300 mb-3">
            Combinaison actuelle ({selectedModules.length}/3)
          </div>

          {selectedModules.length === 0 && (
            <div className="text-xs text-slate-500 italic">
              Aucun module sélectionné
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {selectedModules.map(id => {
              const module = POLICY_MODULES.find(m => m.id === id);
              return (
                <div
                  key={id}
                  className="px-3 py-1.5 rounded-full bg-purple-500/20 border border-purple-500/40 text-xs text-purple-200 font-medium"
                >
                  {module?.name}
                </div>
              );
            })}
          </div>

          <button
            onClick={handleRunSimulation}
            disabled={selectedModules.length !== 3 || isSimulating}
            className={`
              mt-4 w-full py-3 rounded-lg font-semibold transition
              ${selectedModules.length === 3 && !isSimulating
                ? "bg-indigo-600 hover:bg-indigo-500 text-white"
                : "bg-slate-800 text-slate-600 cursor-not-allowed"
              }
            `}
          >
            {isSimulating
              ? "⏳ Simulation en cours..."
              : selectedModules.length === 3
                ? "▶ Lancer la simulation"
                : `Sélectionnez ${3 - selectedModules.length} module(s) supplémentaire(s)`
            }
          </button>
        </div>

        {simulations.length > 0 && (
          <div className="bg-slate-900/40 border border-slate-700 rounded-xl p-4">
            <div className="text-sm font-semibold text-slate-300 mb-3">
              Simulations lancées ({simulations.length})
            </div>

            <div className="space-y-2">
              {simulations.map((sim, i) => {
                const modules = sim.modules || [];

                return (
                  <div
                    key={sim.id}
                    className="flex items-center justify-between p-3 bg-slate-800/40 border border-slate-700 rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-slate-500 font-mono">
                          #{i + 1}
                        </span>
                        <span className="text-[10px] text-slate-600 font-mono">
                          {sim.id}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5 flex-1">
                        {modules.length > 0 ? (
                          modules.map(id => {
                            const module = POLICY_MODULES.find(m => m.id === id);
                            return (
                              <span
                                key={id}
                                className="px-2 py-0.5 rounded bg-purple-500/10 border border-purple-500/30 text-xs text-purple-300"
                              >
                                {module?.name || id}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-xs text-slate-500 italic">
                            Modules manquants
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-emerald-400">
                      ✓ Envoyé
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {simulations.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="text-sm font-semibold text-amber-300 mb-3">
              📊 Validation finale
            </div>
            <div className="text-xs text-slate-400 mb-4">
              Sélectionnez la combinaison recommandée par la supervision IA
            </div>

            <select
              value={validationChoice || ""}
              onChange={(e) => setValidationChoice(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-600 text-white text-sm mb-3"
            >
              <option value="">Choisir une simulation...</option>
              {simulations.map((sim, i) => {
                const modules = sim.modules || [];
                const moduleNames = modules
                  .map(id => POLICY_MODULES.find(m => m.id === id)?.name || id)
                  .join(" + ");

                return (
                  <option key={sim.id} value={sim.id}>
                    Simulation #{i + 1} — {moduleNames || "Modules manquants"}
                  </option>
                );
              })}
            </select>

            <button
              onClick={handleValidate}
              disabled={!validationChoice}
              className={`
                w-full py-3 rounded-lg font-semibold transition
                ${validationChoice
                  ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                  : "bg-slate-800 text-slate-600 cursor-not-allowed"
                }
              `}
            >
              ✓ Valider la politique
            </button>
          </div>
        )}
      </div>
    </div>
  );
}